<template>
  <form class="w-full">
    <h2 class="py-4 text-2xl font-extrabold text-blue">Job Details</h2>
    <div class="flex flex-col gap-4 md:grid md:grid-cols-2">
      <div class="w-full">
        <label for="jobTitle" class="text-md font-extrabold text-grey-70"
          >Job title of Required Staff</label
        >
        <InputText
          class="rounded"
          name="jobTitle"
          required
          type="text"
          :value="jobTitle"
          :error="errors.hasOwnProperty('jobTitle')"
          :error-message="getErrorMessage(errors, 'jobTitle')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'jobTitle',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="w-full">
        <label for="jobLocation" class="text-md font-extrabold text-grey-70"
          >Job location</label
        >
        <InputText
          class="rounded"
          name="jobLocation"
          type="text"
          required
          :value="jobLocation"
          :error="errors.hasOwnProperty('jobLocation')"
          :error-message="getErrorMessage(errors, 'jobLocation')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'jobLocation',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="w-full">
        <label for="employmentType" class="text-md font-extrabold text-grey-70"
          >Employment type</label
        >
        <InputDropdown
          input-classes="w-full rounded"
          name="employmentType"
          placeholder="Employment type"
          :disable-error-popup="true"
          :searchable="false"
          :error="errors.hasOwnProperty('employmentType')"
          :error-message="getErrorMessage(errors, 'employmentType')"
          :items="employmentTypeOptions"
          :value="employmentType"
          @change="
            (value) =>
              this.$emit('changeParameter', { key: 'employmentType', value })
          "
        />
      </div>
      <div class="w-full">
        <label for="doNotContact" class="text-md font-extrabold text-grey-70"
          >Are there any agencies we shouldn't ask to contact you?</label
        >
        <InputText
          class="rounded"
          name="previousAgencies"
          required
          type="text"
          :value="previousAgencies"
          :error="errors.hasOwnProperty('previousAgencies')"
          :error-message="getErrorMessage(errors, 'previousAgencies')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'previousAgencies',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="md:col-span-2">
        <label class="text-md font-extrabold text-grey-70" for="jobDetails"
          >Job details (e.g. salary, start date, etc.)</label
        >
        <InputTextArea
          name="enquiry"
          rows="7"
          required
          :value="enquiry"
          :error="errors.hasOwnProperty('enquiry')"
          :error-message="getErrorMessage(errors, 'enquiry')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'enquiry',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="md:col-span-2">
        <label
          class="text-md block font-extrabold text-grey-70"
          for="jobSpecification"
          >Upload a job specification (optional)</label
        >
        <InputFile
          name="jobSpecification"
          ref="jobSpecificationInput"
          :value="jobSpecification"
          :error="errors.hasOwnProperty('jobSpecification')"
          :error-message="getErrorMessage('jobSpecification')"
          @change="onChangeJobSpecification"
        />
      </div>
      <div class="md:col-span-2">
        <HorizontalSeparator class="border-grey-60 py-2" />
        <h2 class="text-2xl font-extrabold text-blue">Contact Details</h2>
      </div>
      <div class="w-full">
        <label for="companyName" class="text-md font-extrabold text-grey-70"
          >Company name</label
        >
        <InputText
          class="rounded"
          name="companyName"
          type="text"
          required
          :value="companyName"
          :error="errors.hasOwnProperty('companyName')"
          :error-message="getErrorMessage(errors, 'companyName')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'companyName',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="w-full">
        <label for="contactName" class="text-md font-extrabold text-grey-70"
          >Contact Name</label
        >
        <InputText
          class="rounded"
          name="contactName"
          type="text"
          required
          :value="contactName"
          :error="errors.hasOwnProperty('contactName')"
          :error-message="getErrorMessage(errors, 'contactName')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'contactName',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="w-full">
        <label for="email" class="text-md font-extrabold text-grey-70"
          >Email Address</label
        >
        <InputText
          class="rounded"
          name="email"
          type="text"
          required
          :value="email"
          :error="errors.hasOwnProperty('email')"
          :error-message="getErrorMessage(errors, 'email')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'email',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="w-full">
        <label for="phoneNumber" class="text-md font-extrabold text-grey-70"
          >Phone number</label
        >
        <InputText
          class="rounded"
          name="phoneNumber"
          type="text"
          required
          :value="phoneNumber"
          :error="errors.hasOwnProperty('phoneNumber')"
          :error-message="getErrorMessage(errors, 'phoneNumber')"
          @change="
            (event) =>
              this.$emit('changeParameter', {
                key: 'phoneNumber',
                value: event.target.value,
              })
          "
        />
      </div>
      <InputButton
        class="rounded"
        @onClick="this.$emit('submit')"
        styleType="primary"
        :disabled="submitting"
      >
        Submit your requirements
      </InputButton>
    </div>
  </form>
</template>

<script>
import InputButton from 'Components/InputButton.vue';
import InputDropdown from 'Components/InputDropdown.vue';
import HorizontalSeparator from 'Components/HorizontalSeparator';
import InputFile from 'Components/InputFile';
import InputTextArea from 'Components/InputTextArea';
import InputText from 'Components/InputText.vue';
import { getErrorMessage } from 'Helpers/helpers';

export default {
  name: 'EmployerDetailsForm',
  components: {
    HorizontalSeparator,
    InputTextArea,
    InputFile,
    InputButton,
    InputText,
    InputDropdown,
  },
  props: {
    jobTitle: {
      type: String,
      default: '',
    },
    jobLocation: {
      type: String,
      default: '',
    },
    employmentType: {
      type: String,
      default: '',
    },
    previousAgencies: {
      type: String,
      default: '',
    },
    enquiry: {
      type: String,
      default: '',
    },
    jobSpecification: {
      type: String,
      default: '',
    },
    companyName: {
      type: String,
      default: '',
    },
    contactName: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    phoneNumber: {
      type: String,
      default: '',
    },
    errors: {
      type: Object,
      default: {},
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      employmentTypeOptions: {
        all: 'All',
        contractPosition: 'Contract Position',
        permanentPosition: 'Permanent Position',
        tempToPerm: 'Temp To Perm',
        temporaryPosition: 'Temporary Position',
        partTimePosition: 'Part Time Position',
      },
    };
  },
  methods: {
    getErrorMessage,
    onChangeJobSpecification(event) {
      this.$emit('changeParameter', {
        key: 'jobSpecification',
        value: event.target.files[0],
      });
    },
  },
};
</script>
